'use client';

/**
 * Google Ads Table Component
 *
 * API Usage:
 * - /google-ads/customer/detailed - For campaign lists/tables (optimized, no daily data)
 * - /google-ads/campaign/detailed - For detailed daily breakdown (used in separate campaign-details.jsx)
 *
 * This component uses the customer/detailed endpoint for fast table rendering.
 * Individual campaign details with daily data are handled in the campaign-details component.
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import qs from 'qs';
import api from '../../../domain/api';
import { useDebounce } from 'use-debounce';
import { useLocation, useHistory } from 'react-router-dom';
import Select from 'react-select';

const fetchReports = async ({ queryKey }) => {
  const [, filters] = queryKey;

  // If selectedCustomer is provided, add it as customerId filter
  const queryFilters = { ...filters };
  if (filters.selectedCustomer) {
    queryFilters.customerId = filters.selectedCustomer;
    delete queryFilters.selectedCustomer; // Remove selectedCustomer from query as backend expects customerId
  }

  const query = qs.stringify(queryFilters, { arrayFormat: 'brackets' });
  const res = await api.get(`/google-ads?${query}`);
  return res.data;
};

// Function to fetch customer campaign summary (optimized - no daily data)
const fetchCustomerCampaignSummary = async ({ queryKey }) => {
  const [, { customerId, ...filters }] = queryKey;

  if (!customerId) {
    throw new Error('Customer ID is required');
  }

  const requestBody = {
    customerId,
    dateFilter: filters.dateFilter || 'last_30_days',
    page: filters.page || 1,
    limit: filters.limit || 15,
    sortBy: filters.sortBy || 'cost',
    sortOrder: filters.sortOrder || 'desc',
    ...(filters.dateFilter === 'custom' && {
      startDate: filters.startDate,
      endDate: filters.endDate,
    }),
  };

  console.log(
    'Making API call to:',
    `${api.defaults.baseURL}/google-ads/customer/detailed`
  );
  console.log('Request body:', requestBody);

  const res = await api.post('/google-ads/customer/detailed', requestBody);
  return res.data;
};

// Function to fetch Google Ads customers
const fetchGoogleAdsCustomers = async () => {
  const res = await api.get('/google-ads/customers');
  return res.data;
};

const EnhancedGoogleAdsTable = () => {
  const queryClient = useQueryClient();

  const location = useLocation();
  const history = useHistory();
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  // Tab navigation state - initialize from URL params only
  // benchmark modal state
  const [benchmarkModal, setBenchmarkModal] = useState({
    isOpen: false,
    campaign: null,
  });
  const [benchmarkValue, setBenchmarkValue] = useState('');
  const [isSavingBenchmark, setIsSavingBenchmark] = useState(false);

  // CPL Highlighting Logic (similar to Facebook Ads)
  const shouldHighlightRow = (rowData) => {
    const cost = parseFloat(rowData.cost) || 0;
    const conversions = parseFloat(rowData.conversions) || 0;
    const cpl = conversions > 0 ? cost / conversions : 0;

    const campaignBenchmark = parseFloat(rowData.benchmark) || null;
    const clientBenchmark = parseFloat(rowData.client?.benchmark) || null;

    // Use campaign benchmark if available, otherwise use client benchmark
    // If neither exists, don't highlight
    const effectiveBenchmark = campaignBenchmark || clientBenchmark;

    // Highlight if CPL is 0 OR if effective benchmark exists and CPL exceeds it
    return cpl === 0 || (effectiveBenchmark && cpl > effectiveBenchmark);
  };

  const getRowHighlightClass = (rowData) => {
    if (!shouldHighlightRow(rowData)) return '';

    const cost = parseFloat(rowData.cost) || 0;
    const conversions = parseFloat(rowData.conversions) || 0;
    const cpl = conversions > 0 ? cost / conversions : 0;

    // Red for zero CPL (no conversions), orange for exceeding benchmark
    return cpl === 0 ? 'row-highlight-red' : 'row-highlight-orange';
  };

  // Fetch Google Ads customers
  const {
    data: customersData,
    isLoading: customersLoading,
    refetch: refetchCustomers,
  } = useQuery({
    queryKey: ['google-ads-customers'],
    queryFn: fetchGoogleAdsCustomers,
    retry: false, // Disable retry attempts
  });

  const updateBenchmark = async (accountId, campaignId, benchmark) => {
    try {
      const res = await api.post('/benchmarks/upsert', {
        accountId,
        campaign_id: campaignId,
        benchmark,
      });
      return res.data;
    } catch (error) {
      console.error('Error updating benchmark:', error);
      throw error;
    }
  };

  // Create customer options from Google Ads customers API
  const customerOptions = (customersData?.customers || [])
    .filter((customer) => !customer.isManager) // Only show non-manager accounts
    .map((customer) => ({
      label: `${customer.name} (${customer.formattedId})`,
      value: customer.id,
      status: customer.status,
      currency: customer.currencyCode,
    }));

  // Read initial state from URL search params (Facebook Ads style)
  const getInitialFromSearch = () => {
    const params = new URLSearchParams(window.location.search);
    const selectedCustomer = params.get('selectedCustomer') || '';
    const customerId = params.get('customerId') || '';
    const dateFilter = params.get('dateFilter') || 'last_30_days';
    const startDate = params.get('startDate') || '';
    const endDate = params.get('endDate') || '';
    return {
      selectedCustomer: selectedCustomer || customerId,
      dateFilter: { rangeType: dateFilter, from: startDate, to: endDate },
    };
  };

  const initial = getInitialFromSearch();

  const queryParams = useMemo(() => {
    return qs.parse(location.search, { ignoreQueryPrefix: true });
  }, [location.search]);

  const getInitialFilters = () => ({
    accountName: queryParams.accountName || '',
    selectedCustomer:
      initial.selectedCustomer || queryParams.selectedCustomer || '',
    clicksMin: queryParams.clicksMin || '',
    clicksMax: queryParams.clicksMax || '',
    impressionsMin: queryParams.impressionsMin || '',
    impressionsMax: queryParams.impressionsMax || '',
    date: queryParams.date || '',
    dateFilter:
      initial.dateFilter.rangeType || queryParams.dateFilter || 'last_30_days',
    startDate: initial.dateFilter.from || queryParams.startDate || '',
    endDate: initial.dateFilter.to || queryParams.endDate || '',
    customerId: queryParams.customerId || '',
    sortBy: queryParams.sortBy || 'cost',
    sortOrder: queryParams.sortOrder || 'desc',
    page: Number(queryParams.page) || 1,
    limit: Number(queryParams.limit) || 15,
  });

  const [filters, setFilters] = useState(getInitialFilters);

  // Facebook Ads style date filter state
  const [dateFilter, setDateFilter] = useState({
    rangeType: queryParams.dateFilter || 'last_30_days',
    from: queryParams.startDate || '',
    to: queryParams.endDate || '',
  });

  // Function to handle custom date range application (Facebook Ads style)
  const applyCustomDateFilter = () => {
    if (!dateFilter.from || !dateFilter.to) {
      return;
    }

    // Update URL parameters for custom range
    const params = new URLSearchParams(window.location.search);
    if (filters.selectedCustomer) {
      params.set('selectedCustomer', filters.selectedCustomer);
    }
    params.set('dateFilter', 'custom');
    params.set('startDate', dateFilter.from);
    params.set('endDate', dateFilter.to);

    // Preserve the view parameter
    const currentView = params.get('view');
    if (currentView) {
      params.set('view', currentView);
    }

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);

    // Force query refetch
    queryClient.invalidateQueries(['google-ads-campaigns']);
  };

  // URL persistence logic (Facebook Ads style)
  useEffect(() => {
    // Skip URL updates for custom range - handle separately
    if (dateFilter.rangeType === 'custom') {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    if (filters.selectedCustomer) {
      params.set('selectedCustomer', filters.selectedCustomer);
    } else {
      params.delete('selectedCustomer');
    }

    // For preset ranges, update immediately
    if (dateFilter.rangeType) {
      params.set('dateFilter', dateFilter.rangeType);
    } else {
      params.delete('dateFilter');
    }
    // Clear any existing custom date params when switching to presets
    params.delete('startDate');
    params.delete('endDate');

    // Preserve the view parameter
    const currentView = new URLSearchParams(window.location.search).get('view');
    if (currentView) {
      params.set('view', currentView);
    }

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
  }, [filters.selectedCustomer, dateFilter]);

  // Auto-select first customer if none selected and customers are loaded
  useEffect(() => {
    if (
      customerOptions.length > 0 &&
      !filters.selectedCustomer &&
      !filters.customerId &&
      !queryParams.selectedCustomer &&
      !queryParams.customerId
    ) {
      const firstCustomer = customerOptions[0];
      setFilters((f) => ({
        ...f,
        selectedCustomer: firstCustomer.value,
        page: 1,
      }));
    }
  }, [
    customerOptions,
    filters.selectedCustomer,
    filters.customerId,
    queryParams.selectedCustomer,
    queryParams.customerId,
  ]);

  const [debouncedFilters] = useDebounce(filters, 500);
  const [sorting, setSorting] = useState([]);

  const updateURL = useCallback(
    (updatedFilters) => {
      const cleanedFilters = Object.entries(updatedFilters).reduce(
        (acc, [key, val]) => {
          if (val !== '' && val !== undefined && val !== null) {
            acc[key] = val;
          }
          return acc;
        },
        {}
      );

      // Preserve the view parameter from current URL
      const currentParams = new URLSearchParams(location.search);
      const currentView = currentParams.get('view');
      if (currentView) {
        cleanedFilters.view = currentView;
      }

      const query = qs.stringify(cleanedFilters, { addQueryPrefix: true });
      history.replace({ search: query });
    },
    [history, location.search]
  );

  useEffect(() => {
    updateURL(filters);
  }, [filters, updateURL]);

  // Determine which view we're in and which API to use
  const isCustomerCampaignView = !!(
    filters.customerId || filters.selectedCustomer
  );

  // Differentiate between clicked customer (from table) vs selected customer (from dropdown)
  const isClickedCustomerView = !!filters.customerId;

  // Determine query key and function based on current view
  const getQueryConfig = () => {
    if (isCustomerCampaignView) {
      return {
        queryKey: [
          'google-ads-customer-campaigns',
          {
            customerId: filters.customerId || filters.selectedCustomer,
            dateFilter: dateFilter.rangeType,
            page: debouncedFilters.page,
            limit: debouncedFilters.limit,
            sortBy: sorting[0]?.id || debouncedFilters.sortBy,
            sortOrder: sorting[0]?.desc
              ? 'desc'
              : sorting[0]?.desc === false
              ? 'asc'
              : debouncedFilters.sortOrder,
            startDate: dateFilter.from,
            endDate: dateFilter.to,
          },
        ],
        queryFn: fetchCustomerCampaignSummary,
      };
    } else {
      return {
        queryKey: [
          'google-ads-reports',
          {
            ...debouncedFilters,
            sortBy: sorting[0]?.id || debouncedFilters.sortBy,
            sortOrder: sorting[0]?.desc
              ? 'desc'
              : sorting[0]?.desc === false
              ? 'asc'
              : debouncedFilters.sortOrder,
          },
        ],
        queryFn: fetchReports,
      };
    }
  };

  const queryConfig = getQueryConfig();
  const { data, isLoading, isFetching, refetch } = useQuery({
    ...queryConfig,
    retry: false, // Disable retry attempts
    enabled: Boolean(
      (filters.selectedCustomer || filters.customerId) &&
        (dateFilter.rangeType !== 'custom' ||
          (Boolean(dateFilter.from) && Boolean(dateFilter.to)))
    ),
    // refetchOnWindowFocus: false, // Disable refetch on window focus
    // refetchOnMount: false, // Disable refetch on component mount after initial load
    // refetchOnReconnect: false, // Disable refetch on network reconnect
    // keepPreviousData: true,
    // staleTime: Infinity, // Data never becomes stale automatically
  });

  // Handle customer click to show campaign summary
  const handleCustomerClick = useCallback((customerId) => {
    setFilters((f) => ({
      ...f,
      customerId,
      page: 1,
    }));
  }, []);

  // Handle back to reports view
  const handleBackToReports = useCallback(() => {
    setFilters((f) => ({
      ...f,
      customerId: '',
      selectedCustomer: '',
      page: 1,
    }));
  }, []);

  const columns = useMemo(() => {
    // Columns for customer campaigns summary view
    const customerCampaignColumns = [
      {
        header: 'Campaign Name',
        accessorKey: 'name',
        cell: ({ getValue, row }) => (
          <div
            // className='campaign-cell clickable'
            title='Click to view daily details'
            // onClick={() => handleCampaignClick(row.original)}
          >
            {getValue()}
          </div>
        ),
      },

      {
        header: 'Benchmark',
        accessorKey: 'benchmark',
        cell: ({ getValue, row }) => {
          const benchmark = getValue() || row.original.benchmark;
          const isEmpty = !benchmark;
          return (
            <div className='benchmark-cell'>
              <button
                type='button'
                className={`benchmark-edit-button ${
                  isEmpty ? 'benchmark-empty' : ''
                }`}
                onClick={() => {
                  setBenchmarkModal({
                    isOpen: true,
                    campaign: row.original,
                  });
                  setBenchmarkValue(benchmark || '');
                }}
                title='Click to edit benchmark'
              >
                <span className='benchmark-display'>
                  <i className='fa fa-target benchmark-icon'></i>
                  <span className='benchmark-value'>
                    {benchmark || 'Set benchmark'}
                  </span>
                </span>
                <i className='fa fa-edit edit-action-icon'></i>
              </button>
            </div>
          );
        },
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ getValue }) => (
          <div className='status-cell'>
            <span className={`status-badge ${getValue().toLowerCase()}`}>
              {getValue()}
            </span>
          </div>
        ),
      },
      {
        header: 'Impressions',
        accessorKey: 'impressions',
        cell: ({ getValue }) => (
          <div className='metric-cell'>
            {getValue() ? Number.parseInt(getValue()).toLocaleString() : '—'}
          </div>
        ),
      },
      {
        header: 'Clicks',
        accessorKey: 'clicks',
        cell: ({ getValue }) => (
          <div className='metric-cell'>
            {getValue() ? Number.parseInt(getValue()).toLocaleString() : '—'}
          </div>
        ),
      },
      {
        header: 'CTR (%)',
        accessorKey: 'ctr',
        cell: ({ getValue }) => (
          <div className='metric-cell'>
            {getValue() ? Number.parseFloat(getValue()).toFixed(2) + '%' : '—'}
          </div>
        ),
      },
      {
        header: 'Cost',
        accessorKey: 'costFormatted',
        cell: ({ getValue }) => (
          <div className='cost-cell'>
            {getValue() ? `₹${Number.parseFloat(getValue()).toFixed(2)}` : '—'}
          </div>
        ),
      },
      {
        header: 'Conversions',
        accessorKey: 'conversions',
        cell: ({ getValue }) => (
          <div className='conversions-cell'>
            <div className='result-number'>{getValue() || '0'}</div>
            <div className='result-type'>Conversions</div>
          </div>
        ),
      },
      {
        header: 'Cost per Conversion',
        accessorKey: 'cost_per_conversion',
        cell: ({ getValue, row }) => {
          const cost = parseFloat(row.original.cost) || 0;
          const conversions = parseFloat(row.original.conversions) || 0;
          const cpl = conversions > 0 ? cost / conversions : 0;

          const campaignBenchmark = parseFloat(row.original.benchmark) || null;
          const clientBenchmark =
            parseFloat(row.original.client?.benchmark) || null;

          // Use campaign benchmark if available, otherwise use client benchmark
          const effectiveBenchmark = campaignBenchmark || clientBenchmark;

          // Determine if this CPL should be highlighted
          const shouldHighlight =
            cpl === 0 || (effectiveBenchmark && cpl > effectiveBenchmark);
          const highlightClass = shouldHighlight
            ? cpl === 0
              ? 'cpl-highlight-red'
              : 'cpl-highlight-orange'
            : '';

          return (
            <div className={`cost-cell ${highlightClass}`}>
              <div className='cost-amount'>
                {cpl > 0 ? `₹${cpl.toFixed(2)}` : '—'}
              </div>
              <div className='cost-type'>Per conversion</div>
              {shouldHighlight && (
                <div className='cost-warning'>
                  <i className='fa fa-exclamation-triangle'></i>
                  {cpl === 0 ? 'No conversions' : 'Exceeds benchmark'}
                </div>
              )}
            </div>
          );
        },
      },
    ];

    // Standard columns for regular reports
    const standardColumns = [
      {
        header: 'Account Name',
        accessorKey: 'accountName',
        cell: ({ getValue }) => (
          <div className='account-cell' title={getValue()}>
            {getValue()}
          </div>
        ),
      },
      {
        header: 'Customer ID',
        accessorKey: 'customerId',
        cell: ({ getValue, row }) => (
          <div
            className='customer-id-cell clickable'
            title='Click to view detailed campaigns'
            onClick={() => handleCustomerClick(getValue())}
          >
            {getValue()}
          </div>
        ),
      },
      {
        header: 'Ad Group',
        accessorKey: 'adGroup',
        cell: ({ getValue }) => (
          <div className='ad-group-cell' title={getValue()}>
            {getValue()}
          </div>
        ),
      },
      {
        header: 'Clicks',
        accessorKey: 'clicks',
        cell: ({ getValue }) => (
          <div className='metric-cell'>
            {getValue() ? Number.parseInt(getValue()).toLocaleString() : '—'}
          </div>
        ),
      },
      {
        header: 'Impressions',
        accessorKey: 'impressions',
        cell: ({ getValue }) => (
          <div className='metric-cell'>
            {getValue() ? Number.parseInt(getValue()).toLocaleString() : '—'}
          </div>
        ),
      },
      {
        header: 'CTR (%)',
        accessorKey: 'ctr',
        cell: ({ getValue }) => (
          <div className='metric-cell'>
            {getValue() ? Number.parseFloat(getValue()).toFixed(2) + '%' : '—'}
          </div>
        ),
      },
      {
        header: 'Currency',
        accessorKey: 'currencyCode',
        cell: ({ getValue }) => (
          <div className='currency-cell'>{getValue() || '—'}</div>
        ),
      },
      {
        header: 'Cost',
        accessorKey: 'cost',
        cell: ({ getValue }) => (
          <div className='cost-cell'>
            {getValue() ? `₹${Number.parseFloat(getValue()).toFixed(2)}` : '—'}
          </div>
        ),
      },
      {
        header: 'Conversions',
        accessorKey: 'conversions',
        cell: ({ getValue }) => (
          <div className='conversions-cell'>
            <div className='result-number'>{getValue() || '0'}</div>
            <div className='result-type'>Conversions</div>
          </div>
        ),
      },
      {
        header: 'Cost per Conversion',
        accessorKey: 'cost_per_conversion',
        cell: ({ getValue, row }) => {
          const cost = parseFloat(row.original.cost) || 0;
          const conversions = parseFloat(row.original.conversions) || 0;
          const cpl = conversions > 0 ? cost / conversions : 0;

          const campaignBenchmark = parseFloat(row.original.benchmark) || null;
          const clientBenchmark =
            parseFloat(row.original.client?.benchmark) || null;

          // Use campaign benchmark if available, otherwise use client benchmark
          const effectiveBenchmark = campaignBenchmark || clientBenchmark;

          // Determine if this CPL should be highlighted
          const shouldHighlight =
            cpl === 0 || (effectiveBenchmark && cpl > effectiveBenchmark);
          const highlightClass = shouldHighlight
            ? cpl === 0
              ? 'cpl-highlight-red'
              : 'cpl-highlight-orange'
            : '';

          return (
            <div className={`cost-cell ${highlightClass}`}>
              <div className='cost-amount'>
                {cpl > 0 ? `₹${cpl.toFixed(2)}` : '—'}
              </div>
              <div className='cost-type'>Per conversion</div>
              {shouldHighlight && (
                <div className='cost-warning'>
                  <i className='fa fa-exclamation-triangle'></i>
                  {cpl === 0 ? 'No conversions' : 'Exceeds benchmark'}
                </div>
              )}
            </div>
          );
        },
      },
      {
        header: 'Client',
        accessorKey: 'client.name',
        cell: ({ row }) => (
          <div className='client-cell'>
            {row.original.client?.name || 'N/A'}
          </div>
        ),
      },
      {
        header: 'Report Date',
        accessorKey: 'reportDate',
        cell: ({ getValue }) => (
          <div className='date-cell'>
            {new Date(getValue()).toLocaleDateString()}
          </div>
        ),
      },
    ];

    return isCustomerCampaignView ? customerCampaignColumns : standardColumns;
  }, [isCustomerCampaignView, handleCustomerClick]);

  const table = useReactTable({
    data: isCustomerCampaignView ? data?.campaigns || [] : data?.reports || [],
    columns,
    pageCount: isCustomerCampaignView
      ? data?.pagination?.totalPages || -1
      : data?.totalPages || -1,
    manualPagination: true,
    manualSorting: true,
    state: {
      pagination: {
        pageIndex: (filters.page || 1) - 1,
        pageSize: filters.limit,
      },
      sorting,
    },
    onPaginationChange: (updater) => {
      const newState =
        typeof updater === 'function'
          ? updater(table.getState().pagination)
          : updater;
      setFilters((f) => ({
        ...f,
        page: newState.pageIndex + 1,
        limit: newState.pageSize,
      }));
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value, page: 1 }));
  };

  const clearAllFilters = () => {
    setFilters({
      accountName: '',
      selectedCustomer: '',
      clicksMin: '',
      clicksMax: '',
      impressionsMin: '',
      impressionsMax: '',
      date: '',
      dateFilter: 'last_30_days',
      startDate: '',
      endDate: '',
      customerId: '',
      sortBy: 'cost',
      sortOrder: 'desc',
      page: 1,
      limit: 15,
    });

    // Also clear date filter
    setDateFilter({
      rangeType: 'last_30_days',
      from: '',
      to: '',
    });
  };

  const activeFiltersCount = Object.values(filters).filter(
    (value, index) =>
      value !== '' &&
      value !== null &&
      value !== undefined &&
      index < Object.keys(filters).length - 2
  ).length;

  return (
    <>
      <style jsx>{`
        .campaign-analytics-container {
          background: #f5f6f7;
          min-height: 100vh;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
            Helvetica, Arial, sans-serif;
        }

        .google-ads-header {
          background: linear-gradient(
            135deg,
            #4285f4 0%,
            #1a73e8 50%,
            #3367d6 100%
          );
          color: white;
          padding: 12px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          box-shadow: 0 4px 16px rgba(66, 133, 244, 0.2);
          border-bottom: 2px solid #3367d6;
          position: relative;
          overflow: hidden;
        }

        .google-ads-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.1),
            transparent
          );
          animation: shimmer 3s infinite;
        }

        @keyframes shimmer {
          0% {
            left: -100%;
          }
          100% {
            left: 100%;
          }
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 16px;
          flex: 1;
        }

        .header-title-section {
          display: flex;
          align-items: center;
        }

        .header-title {
          font-size: 20px;
          font-weight: 700;
          margin: 0;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          letter-spacing: 0.3px;
          display: flex;
          align-items: center;
          gap: 10px;
          position: relative;
          z-index: 1;
        }

        .title-content {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .header-title i {
          font-size: 18px;
          opacity: 0.95;
          flex-shrink: 0;
        }

        .title-text {
          font-weight: 700;
          color: white;
          white-space: nowrap;
        }

        .header-subtitle {
          font-size: 11px;
          opacity: 0.75;
          font-weight: 400;
          font-style: italic;
          color: rgba(255, 255, 255, 0.8);
          white-space: nowrap;
        }

        /* Clean Customer Details Styles */
        .customer-details-clean {
          margin: 16px 24px 0 24px;
          background: #ffffff;
          border: 1px solid #e8eaed;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(60, 64, 67, 0.12);
        }

        .customer-header {
          padding: 16px 20px;
          border-bottom: 1px solid #f1f3f4;
          background: linear-gradient(135deg, #fafbfc 0%, #f8f9fa 100%);
        }

        .customer-info {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
        }

        .customer-name {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #202124;
          letter-spacing: -0.2px;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 16px;
          font-size: 12px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .status-badge.enabled {
          background: #e8f5e8;
          color: #137333;
          border: 1px solid #c8e6c8;
        }

        .status-badge.paused {
          background: #fef3e0;
          color: #b06000;
          border: 1px solid #fdd663;
        }

        .status-badge i {
          font-size: 8px;
        }

        .customer-meta {
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: #5f6368;
          font-weight: 500;
        }

        .meta-item i {
          font-size: 12px;
          width: 14px;
          text-align: center;
          color: #9aa0a6;
        }

        .client-selector-header {
          min-width: 360px;
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .selector-label {
          font-size: 12px;
          font-weight: 600;
          opacity: 0.9;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 2px;
        }

        .back-button {
          background: rgba(255, 255, 255, 0.15);
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: white;
          border-radius: 6px;
          padding: 8px 10px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          transition: all 0.3s ease;
          position: relative;
          z-index: 1;
          backdrop-filter: blur(10px);
        }

        .back-button:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: translateX(-3px) scale(1.05);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .back-button:active {
          transform: translateX(-1px) scale(0.98);
        }

        .google-ads-content {
          background: white;
          margin: 0;
          border-radius: 0;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .toolbar {
          padding: 10px 20px;
          border-bottom: 1px solid #e4e6ea;
          display: flex;
          align-items: center;
          gap: 8px;
          background: #fafbfc;
        }

        .toolbar-button {
          background: #4285f4;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 4px;
          transition: all 0.2s;
        }

        .toolbar-button.secondary {
          background: #e4e6ea;
          color: #1c1e21;
        }

        .toolbar-button:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .toolbar-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .filters-toggle {
          background: #e4e6ea;
          color: #1c1e21;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 4px;
          transition: all 0.2s;
        }

        .filters-toggle:hover {
          background: #d1d5db;
        }

        .background-loading {
          position: relative;
          background: rgba(66, 133, 244, 0.05);
          border-bottom: 1px solid rgba(66, 133, 244, 0.1);
          padding: 8px 20px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .background-loading-content {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #4285f4;
          font-size: 13px;
          font-weight: 500;
        }

        .background-loading-spinner {
          width: 14px;
          height: 14px;
          border: 2px solid rgba(66, 133, 244, 0.2);
          border-top: 2px solid #4285f4;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .filters-badge {
          background: #4285f4;
          color: white;
          border-radius: 10px;
          padding: 2px 6px;
          font-size: 10px;
          margin-left: 4px;
        }

        .search-container {
          margin-left: auto;
          position: relative;
        }

        .search-input {
          padding: 6px 10px 6px 30px;
          border: 1px solid #ccd0d5;
          border-radius: 4px;
          font-size: 13px;
          width: 200px;
        }

        .search-icon {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          color: #65676b;
          font-size: 12px;
        }

        .filters-section {
          background: #f8f9fa;
          border-bottom: 1px solid #e4e6ea;
          padding: 12px 20px;
          display: ${isFiltersExpanded ? 'block' : 'none'};
        }

        .filters-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 12px;
          margin-bottom: 12px;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .filter-label {
          font-size: 11px;
          font-weight: 600;
          color: #65676b;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .filter-input {
          border: 1px solid #ccd0d5;
          border-radius: 4px;
          padding: 6px 8px;
          font-size: 13px;
          height: 32px;
        }

        .filter-input:focus {
          outline: none;
          border-color: #4285f4;
          box-shadow: 0 0 0 2px rgba(66, 133, 244, 0.1);
        }

        .range-group {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
        }

        .clear-filters-btn {
          background: #dc3545;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 4px;
          height: 32px;
          align-self: end;
        }

        .clear-filters-btn:hover {
          background: #c82333;
        }

        .apply-date-btn {
          background: #28a745;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 4px;
          height: 32px;
          align-self: end;
          transition: all 0.2s;
        }

        .apply-date-btn:hover:not(:disabled) {
          background: #218838;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .apply-date-btn:disabled {
          background: #6c757d;
          cursor: not-allowed;
          opacity: 0.6;
        }

        .summary-section {
          background: #f8f9fa;
          border-bottom: 1px solid #dadce0;
          padding: 12px 20px;
        }

        .summary-period {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 10px;
          padding: 6px 10px;
          background: #e8f0fe;
          border-radius: 4px;
          border: 1px solid #d2e3fc;
          color: #1565c0;
          font-size: 12px;
          font-weight: 600;
        }

        .summary-period i {
          color: #1976d2;
        }

        .summary-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 8px;
        }

        .summary-card {
          background: white;
          border: 1px solid #dadce0;
          border-radius: 6px;
          padding: 8px 12px;
          text-align: center;
          transition: all 0.2s ease;
          box-shadow: 0 1px 2px rgba(60, 64, 67, 0.1);
        }

        .summary-card:hover {
          box-shadow: 0 2px 6px rgba(60, 64, 67, 0.15);
          transform: translateY(-1px);
          border-color: #4285f4;
        }

        .summary-label {
          font-size: 10px;
          color: #5f6368;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          margin-bottom: 3px;
          font-weight: 600;
          line-height: 1.2;
        }

        .summary-value {
          font-size: 15px;
          font-weight: 600;
          color: #202124;
          line-height: 1.2;
        }

        .google-table-container {
          position: relative;
          overflow-x: auto;
          max-height: calc(100vh - 200px);
          border: 1px solid #e4e6ea;
        }

        .google-table {
          width: 100%;
          min-width: 1400px;
          border-collapse: collapse;
          background: white;
          font-size: 13px;
        }

        .google-table-header {
          background: #f8f9fa;
          border-bottom: 2px solid #e4e6ea;
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .google-table-header th {
          padding: 8px 12px;
          text-align: left;
          font-size: 11px;
          font-weight: 600;
          color: #65676b;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          cursor: pointer;
          white-space: nowrap;
          border-right: 1px solid #e4e6ea;
        }

        .google-table-header th:last-child {
          border-right: none;
        }

        .google-table-header th:hover {
          background: #e9ecef;
        }

        .google-table-body tr {
          border-bottom: 1px solid #f0f2f5;
        }

        .google-table-body tr:hover {
          background: #f8f9fa;
        }

        .google-table-body td {
          padding: 8px 12px;
          font-size: 13px;
          color: #1c1e21;
          vertical-align: top;
          border-right: 1px solid #f0f2f5;
        }

        .google-table-body td:last-child {
          border-right: none;
        }

        .account-cell,
        .ad-group-cell {
          max-width: 200px;
          word-wrap: break-word;
          white-space: normal;
          line-height: 1.3;
        }

        .customer-id-cell {
          font-family: 'Courier New', monospace;
          font-size: 12px;
          color: #65676b;
          min-width: 120px;
          word-wrap: break-word;
          white-space: normal;
          line-height: 1.2;
        }

        .customer-id-cell.clickable {
          color: #4285f4;
          cursor: pointer;
          text-decoration: underline;
          transition: all 0.2s;
        }

        .customer-id-cell.clickable:hover {
          color: #3367d6;
          background: rgba(66, 133, 244, 0.1);
          padding: 4px 8px;
          border-radius: 4px;
        }

        .campaign-cell {
          max-width: 250px;
          word-wrap: break-word;
          white-space: normal;
          line-height: 1.3;
          font-weight: 500;
        }

        .campaign-cell.clickable {
          color: #4285f4;
          cursor: pointer;
          text-decoration: underline;
          transition: all 0.2s;
        }

        .campaign-cell.clickable:hover {
          color: #3367d6;
          background: rgba(66, 133, 244, 0.1);
          padding: 4px 8px;
          border-radius: 4px;
        }

        .status-cell {
          text-align: center;
          min-width: 80px;
        }

        .status-badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .status-badge.enabled {
          background: #d1fae5;
          color: #065f46;
        }

        .status-badge.paused {
          background: #fef3c7;
          color: #92400e;
        }

        .status-badge.removed {
          background: #fee2e2;
          color: #991b1b;
        }

        .metric-cell {
          text-align: right;
          font-weight: 500;
          min-width: 70px;
        }

        .cost-cell {
          text-align: right;
          font-weight: 600;
          min-width: 80px;
        }

        .conversions-cell {
          text-align: center;
          min-width: 80px;
        }

        .result-number {
          font-weight: 600;
          font-size: 14px;
          color: #1c1e21;
        }

        .result-type {
          font-size: 10px;
          color: #65676b;
          margin-top: 1px;
        }

        .currency-cell {
          text-align: center;
          font-weight: 500;
          min-width: 60px;
        }

        .client-cell {
          font-weight: 500;
          color: #4285f4;
        }

        .date-cell {
          font-size: 12px;
          color: #65676b;
          min-width: 90px;
        }

        .pagination-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 20px;
          background: #fafbfc;
          border-top: 1px solid #e4e6ea;
          font-size: 13px;
        }

        .pagination-info {
          color: #65676b;
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .pagination-button {
          background: white;
          border: 1px solid #ccd0d5;
          color: #1c1e21;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s;
          min-width: 32px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .pagination-button:hover:not(:disabled) {
          background: #f5f6f7;
          border-color: #8a8d91;
        }

        .pagination-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pagination-button.active {
          background: #4285f4;
          color: white;
          border-color: #4285f4;
        }

        .page-size-selector {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-left: 16px;
        }

        .page-size-select {
          padding: 4px 8px;
          border: 1px solid #ccd0d5;
          border-radius: 4px;
          font-size: 12px;
          background: white;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          color: #65676b;
        }

        .loading-spinner {
          width: 24px;
          height: 24px;
          border: 2px solid #f0f2f5;
          border-top: 2px solid #4285f4;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 12px;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .react-select-container .react-select__control {
          border: 1px solid rgba(255, 255, 255, 0.3);
          background-color: rgba(255, 255, 255, 0.1);
          min-height: 32px;
          border-radius: 4px;
        }

        .react-select-container .react-select__control:hover {
          border-color: rgba(255, 255, 255, 0.5);
        }

        .react-select-container .react-select__control--is-focused {
          border-color: white;
          box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
        }

        .react-select-container .react-select__placeholder {
          color: rgba(255, 255, 255, 0.8);
          font-size: 13px;
        }

        .react-select-container .react-select__single-value {
          color: white;
          font-size: 13px;
        }

        .react-select-container .react-select__menu {
          z-index: 1000;
          border-radius: 4px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
        }

        .react-select-container .react-select__option--is-focused {
          background-color: #f5f6f7;
        }

        .react-select-container .react-select__option--is-selected {
          background-color: #4285f4;
        }

        .sort-icon {
          margin-left: 4px;
          font-size: 10px;
        }

        /* Horizontal scroll styling */
        .google-table-container::-webkit-scrollbar {
          height: 8px;
        }

        .google-table-container::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        .google-table-container::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
        }

        .google-table-container::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }

        @media (max-width: 768px) {
          .google-ads-header {
            flex-direction: column;
            gap: 8px;
            align-items: stretch;
            padding: 10px 16px;
          }

          .toolbar {
            flex-wrap: wrap;
            padding: 8px 16px;
          }

          .search-container {
            margin-left: 0;
            margin-top: 8px;
          }

          .search-input {
            width: 150px;
          }

          .pagination-container {
            flex-direction: column;
            gap: 8px;
            align-items: stretch;
          }

          .pagination-controls {
            justify-content: center;
          }

          .filters-grid {
            grid-template-columns: 1fr;
          }

          /* Clean Customer Details Mobile Styles */
          .customer-details-clean {
            margin: 12px 16px 0 16px;
          }

          .customer-header {
            padding: 12px 16px;
          }

          .customer-info {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            margin-bottom: 10px;
          }

          .customer-meta {
            gap: 12px;
          }

          .meta-item {
            font-size: 12px;
          }

          .summary-cards {
            grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
            gap: 6px;
          }

          .summary-card {
            padding: 6px 8px;
          }

          .summary-label {
            font-size: 9px;
          }

          .summary-value {
            font-size: 13px;
          }
        }

        .benchmark-cell {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 140px;
        }

        .benchmark-edit-button {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border: 1px solid #dee2e6;
          border-radius: 6px;
          padding: 6px 10px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 13px;
          color: #495057;
          min-width: 120px;
          position: relative;
          overflow: hidden;
        }

        .benchmark-edit-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.4),
            transparent
          );
          transition: left 0.5s ease;
        }

        .benchmark-edit-button:hover::before {
          left: 100%;
        }

        .benchmark-edit-button:hover {
          background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
          border-color: #1877f2;
          color: #1877f2;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(24, 119, 242, 0.15);
        }

        .benchmark-edit-button:active {
          transform: translateY(0);
          box-shadow: 0 1px 3px rgba(24, 119, 242, 0.2);
        }

        .benchmark-display {
          display: flex;
          align-items: center;
          gap: 6px;
          flex: 1;
        }

        .benchmark-icon {
          color: #6c757d;
          font-size: 12px;
          transition: color 0.2s ease;
        }

        .benchmark-edit-button:hover .benchmark-icon {
          color: #1877f2;
        }

        .benchmark-value {
          font-weight: 500;
          color: inherit;
          font-size: 13px;
        }

        .benchmark-edit-button .benchmark-value {
          color: #495057;
        }

        .benchmark-edit-button:hover .benchmark-value {
          color: #1877f2;
        }

        .edit-action-icon {
          color: #6c757d;
          font-size: 11px;
          margin-left: 8px;
          opacity: 0.7;
          transition: all 0.2s ease;
        }

        .benchmark-edit-button:hover .edit-action-icon {
          color: #1877f2;
          opacity: 1;
          transform: scale(1.1);
        }

        /* Special styling for empty benchmark */
        .benchmark-edit-button.benchmark-empty {
          background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
          border-color: #ffc107;
          color: #856404;
        }

        .benchmark-edit-button.benchmark-empty:hover {
          background: linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%);
          border-color: #e0a800;
          color: #533f03;
        }

        .benchmark-edit-button.benchmark-empty .benchmark-icon {
          color: #856404;
        }

        .benchmark-edit-button.benchmark-empty:hover .benchmark-icon {
          color: #533f03;
        }

        .benchmark-edit-button.benchmark-empty .edit-action-icon {
          color: #856404;
        }

        .benchmark-edit-button.benchmark-empty:hover .edit-action-icon {
          color: #533f03;
        }

        .benchmark-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
        }

        .benchmark-modal {
          background: white;
          border-radius: 8px;
          padding: 0;
          max-width: 500px;
          width: 90%;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
        }

        .modal-header {
          padding: 16px 20px;
          border-bottom: 1px solid #e4e6ea;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .modal-title {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #1c1e21;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 18px;
          color: #65676b;
          cursor: pointer;
          padding: 4px;
          line-height: 1;
        }

        .modal-close:hover {
          color: #1c1e21;
        }

        .modal-body {
          padding: 16px 20px;
        }

        .form-group {
          margin-bottom: 12px;
        }

        .form-label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
          color: #1c1e21;
          font-size: 14px;
        }

        .benchmark-input {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid #ccd0d5;
          border-radius: 6px;
          font-size: 14px;
          transition: border-color 0.2s;
          box-sizing: border-box;
        }

        .benchmark-input:focus {
          outline: none;
          border-color: #1877f2;
          box-shadow: 0 0 0 2px rgba(24, 119, 242, 0.2);
        }

        .modal-footer {
          padding: 12px 20px;
          border-top: 1px solid #e4e6ea;
          display: flex;
          gap: 10px;
          justify-content: flex-end;
        }

        .save-benchmark-btn {
          background: #1877f2;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .save-benchmark-btn:hover:not(:disabled) {
          background: #166fe5;
        }

        .save-benchmark-btn:disabled {
          background: #ccd0d5;
          cursor: not-allowed;
        }

        .cancel-benchmark-btn {
          background: #e4e6ea;
          color: #1c1e21;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .cancel-benchmark-btn:hover {
          background: #d8dadf;
        }

        /* Facebook Ads style date filter styles */
        .date-filter-container {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-bottom: 1px solid #e4e6ea;
          border-radius: 8px;
          padding: 12px 20px;
          margin-bottom: 12px;
        }

        .date-filter-wrapper {
          display: flex;
          align-items: center;
          gap: 16px;
          max-width: 1200px;
        }

        .date-filter-label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          font-weight: 600;
          color: #495057;
          min-width: 100px;
        }

        .date-filter-label i {
          color: #1877f2;
        }

        .date-filter-controls {
          display: flex;
          align-items: center;
          gap: 16px;
          flex: 1;
        }

        .date-preset-buttons {
          display: flex;
          gap: 4px;
          background: white;
          border-radius: 6px;
          padding: 2px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e4e6ea;
        }

        .date-preset-btn {
          background: transparent;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          color: #495057;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .date-preset-btn:hover {
          background: #f8f9fa;
          color: #1877f2;
        }

        .date-preset-btn.active {
          background: #1877f2;
          color: white;
          box-shadow: 0 1px 2px rgba(24, 119, 242, 0.2);
        }

        .custom-date-inputs {
          display: flex;
          gap: 12px;
          align-items: center;
          background: white;
          padding: 8px 12px;
          border-radius: 6px;
          border: 1px solid #e4e6ea;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          position: relative;
          z-index: 5;
          pointer-events: auto;
          isolation: isolate;
        }

        .date-input-group {
          display: flex;
          align-items: center;
          gap: 6px;
          position: relative;
          z-index: 6;
        }

        .date-input-group label {
          font-size: 12px;
          font-weight: 500;
          color: #495057;
          min-width: 35px;
        }

        .date-input {
          border: 1px solid #ced4da;
          border-radius: 4px;
          padding: 6px 8px;
          font-size: 12px;
          color: #495057;
          background: white;
          min-width: 120px;
        }

        .date-input:focus {
          outline: none;
          border-color: #1877f2;
          box-shadow: 0 0 0 2px rgba(24, 119, 242, 0.1);
        }

        /* Firefox date input styling */
        .date-input::-moz-focus-inner {
          border: none;
        }

        /* Custom Calendar Styles */
        .calendar-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border: 1px solid #e4e6ea;
          border-radius: 8px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
          z-index: 1000;
          margin-top: 4px;
          min-width: 280px;
        }

        .calendar-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          border-bottom: 1px solid #f0f2f5;
          background: #f8f9fa;
          border-radius: 8px 8px 0 0;
        }

        .calendar-nav-btn {
          background: none;
          border: none;
          color: #65676b;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 14px;
          transition: all 0.2s ease;
        }

        .calendar-nav-btn:hover {
          background: #e4e6ea;
          color: #1c1e21;
        }

        .calendar-month-year {
          font-weight: 600;
          color: #1c1e21;
          font-size: 14px;
        }

        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 1px;
          background: #f0f2f5;
          border-radius: 0 0 8px 8px;
          overflow: hidden;
        }

        .calendar-day-header {
          background: #f8f9fa;
          padding: 8px 4px;
          text-align: center;
          font-size: 11px;
          font-weight: 600;
          color: #65676b;
          text-transform: uppercase;
        }

        .calendar-day {
          background: white;
          padding: 8px;
          text-align: center;
          cursor: pointer;
          font-size: 13px;
          transition: all 0.2s ease;
          min-height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .calendar-day:hover {
          background: #f0f2f5;
        }

        .calendar-day.other-month {
          color: #bcc0c4;
        }

        .calendar-day.today {
          background: #e3f2fd;
          color: #1877f2;
          font-weight: 600;
        }

        .calendar-day.selected {
          background: #1877f2;
          color: white;
          font-weight: 600;
        }

        .calendar-day.selected:hover {
          background: #166fe5;
        }

        .date-input-button {
          border: 1px solid #ced4da;
          border-radius: 4px;
          padding: 6px 12px;
          font-size: 12px;
          color: #495057;
          background: white;
          min-width: 140px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: relative;
          transition: all 0.2s ease;
        }

        .date-input-button:hover {
          border-color: #1877f2;
        }

        .date-input-button:focus {
          outline: none;
          border-color: #1877f2;
          box-shadow: 0 0 0 2px rgba(24, 119, 242, 0.1);
        }

        .date-input-button.has-value {
          color: #1c1e21;
        }

        .date-input-button .calendar-icon {
          color: #65676b;
          font-size: 12px;
          margin-left: 8px;
        }

        .date-input-wrapper {
          position: relative;
          display: inline-block;
        }

        .date-input-row {
          display: flex;
          align-items: center;
          gap: 12px;
          position: relative;
          z-index: 5;
        }

        .apply-filter-btn {
          background: #1877f2;
          color: white;
          border: none;
          padding: 6px 16px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .apply-filter-btn:hover:not(:disabled) {
          background: #166fe5;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(24, 119, 242, 0.2);
        }

        .apply-filter-btn:disabled {
          background: #9ca3af;
          cursor: not-allowed;
          opacity: 0.6;
        }

        @media (max-width: 768px) {
          .date-filter-wrapper {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          .date-preset-buttons {
            flex-wrap: wrap;
          }

          .custom-date-inputs {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          .date-input-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }
        }

        /* CPL Highlighting Styles */
        .cpl-highlight-red {
          background: linear-gradient(135deg, #ffeaea 0%, #ffcccc 100%);
          border-left: 3px solid #dc3545;
          position: relative;
        }

        .cpl-highlight-orange {
          background: linear-gradient(135deg, #fff3e0 0%, #ffe0b3 100%);
          border-left: 3px solid #ff9800;
          position: relative;
        }

        .cost-warning {
          font-size: 9px;
          color: #dc3545;
          margin-top: 2px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 2px;
        }

        .cpl-highlight-orange .cost-warning {
          color: #f57c00;
        }

        /* Row Highlighting Styles */
        .row-highlight-red {
          background: linear-gradient(
            135deg,
            #ffeaea 0%,
            #ffcccc 100%
          ) !important;
          border-left: 4px solid #dc3545;
        }

        .row-highlight-orange {
          background: linear-gradient(
            135deg,
            #fff3e0 0%,
            #ffe0b3 100%
          ) !important;
          border-left: 4px solid #ff9800;
        }

        .row-highlight-red:hover,
        .row-highlight-orange:hover {
          opacity: 0.9;
        }

        /* Subtle animation for highlighted rows */
        .row-highlight-red,
        .row-highlight-orange {
          animation: highlightPulse 2s ease-in-out infinite;
        }

        @keyframes highlightPulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.95;
          }
        }
      `}</style>

      <div className='campaign-analytics-container'>
        <div className='google-ads-header'>
          <div className='header-left'>
            {isClickedCustomerView && (
              <button
                className='back-button'
                onClick={handleBackToReports}
                title='Back to Reports'
              >
                <i className='fa fa-arrow-left'></i>
              </button>
            )}
            <div className='header-title-section'>
              <h1 className='header-title'>
                <i className='fab fa-google'></i>
                <div className='title-content'>
                  <span className='title-text'>
                    {isCustomerCampaignView
                      ? 'Customer Campaigns'
                      : 'Google Ads Reports'}
                  </span>
                  <span className='header-subtitle'>
                    {isCustomerCampaignView
                      ? 'View campaign performance metrics'
                      : 'Comprehensive advertising analytics dashboard'}
                  </span>
                </div>
              </h1>
            </div>
            {isCustomerCampaignView && data?.customer && (
              <div className='customer-details-clean'>
                <div className='customer-header'>
                  <div className='customer-info'>
                    <h3 className='customer-name'>{data.customer.name}</h3>
                    <span
                      className={`status-badge ${data.customer.status?.toLowerCase()}`}
                    >
                      <i className='fa fa-circle'></i>
                      {data.customer.status === 'ENABLED' ? 'Active' : 'Paused'}
                    </span>
                  </div>
                  <div className='customer-meta'>
                    <span className='meta-item'>
                      <i className='fa fa-tag'></i>
                      ID: {data.customer.formattedId || data.customer.id}
                    </span>
                    <span className='meta-item'>
                      <i className='fa fa-globe'></i>
                      {data.customer.currencyCode}
                    </span>
                    <span className='meta-item'>
                      <i className='fa fa-clock'></i>
                      {data.customer.timeZone?.split('/')[1] ||
                        data.customer.timeZone}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className='client-selector-header'>
            <div className='selector-label'>Customer Selection</div>
            <Select
              className='react-select-container'
              classNamePrefix='react-select'
              options={customerOptions}
              value={
                customerOptions.find(
                  (c) =>
                    c.value === (filters.customerId || filters.selectedCustomer)
                ) || null
              }
              onChange={(selected) =>
                setFilters((f) => ({
                  ...f,
                  selectedCustomer: selected ? selected.value : '',
                  customerId: '', // Clear customerId when selecting from dropdown
                  page: 1,
                }))
              }
              placeholder={
                customersLoading ? 'Loading customers...' : 'Select customer...'
              }
              isLoading={customersLoading}
              isDisabled={customersLoading}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              formatOptionLabel={(option) => (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>{option.label}</span>
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center',
                      fontSize: '11px',
                    }}
                  >
                    <span
                      style={{
                        background:
                          option.status === 'ENABLED' ? '#10b981' : '#ef4444',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '4px',
                      }}
                    >
                      {option.status}
                    </span>
                    <span style={{ color: '#6b7280', fontWeight: '600' }}>
                      {option.currency}
                    </span>
                  </div>
                </div>
              )}
            />
          </div>
        </div>

        <div className='google-ads-content'>
          <div className='toolbar'>
            {/* <button
              className='toolbar-button'
              onClick={() => history.push('/adsreports/upload-google-report')}
            >
              <i className='fa fa-plus'></i>
              Create
            </button> */}
            <button
              className='toolbar-button secondary'
              onClick={() => refetch()}
              disabled={isLoading || isFetching}
              title='Refresh data'
            >
              <i
                className={`fa fa-refresh ${
                  isLoading || isFetching ? 'fa-spin' : ''
                }`}
              ></i>
              Refresh
            </button>
            <button
              className='toolbar-button secondary'
              onClick={() => refetchCustomers()}
              disabled={customersLoading}
              title='Refresh customers list'
            >
              <i
                className={`fa fa-users ${customersLoading ? 'fa-spin' : ''}`}
              ></i>
              Refresh Customers
            </button>
            <button
              className='filters-toggle'
              onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
            >
              <i className='fa fa-filter'></i>
              Filters
              {activeFiltersCount > 0 && (
                <span className='filters-badge'>{activeFiltersCount}</span>
              )}
              <i
                className={`fa fa-chevron-${isFiltersExpanded ? 'up' : 'down'}`}
              ></i>
            </button>
            <div className='search-container'>
              <i className='fa fa-search search-icon'></i>
              <input
                type='text'
                className='search-input'
                placeholder='Search accounts...'
                name='accountName'
                value={filters.accountName}
                onChange={handleFilterChange}
              />
            </div>
          </div>

          {/* Background loading indicator */}
          {isFetching && !isLoading && (
            <div className='background-loading'>
              <div className='background-loading-content'>
                <div className='background-loading-spinner'></div>
                <span>Fetching latest data...</span>
              </div>
            </div>
          )}

          {isFiltersExpanded && (
            <div className='filters-section'>
              {isCustomerCampaignView && (
                <div className='date-filter-container'>
                  <div className='date-filter-wrapper'>
                    <div className='date-filter-label'>
                      <i className='fa fa-calendar'></i>
                      Date Range:
                    </div>
                    <div className='date-filter-controls'>
                      <div className='date-preset-buttons'>
                        <button
                          className={`date-preset-btn ${
                            dateFilter.rangeType === 'yesterday' ? 'active' : ''
                          }`}
                          onClick={() => {
                            setDateFilter({
                              rangeType: 'yesterday',
                              from: '',
                              to: '',
                            });
                          }}
                        >
                          Yesterday
                        </button>
                        <button
                          className={`date-preset-btn ${
                            dateFilter.rangeType === 'last_7_days'
                              ? 'active'
                              : ''
                          }`}
                          onClick={() => {
                            setDateFilter({
                              rangeType: 'last_7_days',
                              from: '',
                              to: '',
                            });
                          }}
                        >
                          7 Days
                        </button>
                        <button
                          className={`date-preset-btn ${
                            dateFilter.rangeType === 'last_14_days'
                              ? 'active'
                              : ''
                          }`}
                          onClick={() => {
                            setDateFilter({
                              rangeType: 'last_14_days',
                              from: '',
                              to: '',
                            });
                          }}
                        >
                          14 Days
                        </button>
                        <button
                          className={`date-preset-btn ${
                            dateFilter.rangeType === 'last_30_days'
                              ? 'active'
                              : ''
                          }`}
                          onClick={() => {
                            setDateFilter({
                              rangeType: 'last_30_days',
                              from: '',
                              to: '',
                            });
                          }}
                        >
                          30 Days
                        </button>
                        <button
                          className={`date-preset-btn ${
                            dateFilter.rangeType === 'custom' ? 'active' : ''
                          }`}
                          onClick={() => {
                            setDateFilter({
                              ...dateFilter,
                              rangeType: 'custom',
                            });
                          }}
                        >
                          Custom Range
                        </button>
                      </div>
                      {dateFilter.rangeType === 'custom' && (
                        <div className='custom-date-inputs'>
                          <div className='date-input-row'>
                            <div className='date-input-group'>
                              <label>From:</label>
                              <input
                                type='date'
                                className='date-input'
                                value={dateFilter.from}
                                onChange={(e) =>
                                  setDateFilter({
                                    ...dateFilter,
                                    from: e.target.value,
                                  })
                                }
                              />
                            </div>
                            <div className='date-input-group'>
                              <label>To:</label>
                              <input
                                type='date'
                                className='date-input'
                                value={dateFilter.to}
                                onChange={(e) =>
                                  setDateFilter({
                                    ...dateFilter,
                                    to: e.target.value,
                                  })
                                }
                              />
                            </div>
                            <button
                              className='apply-filter-btn'
                              onClick={applyCustomDateFilter}
                              disabled={!dateFilter.from || !dateFilter.to}
                            >
                              Apply Filter
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className='filters-grid'>
                {isCustomerCampaignView ? (
                  <>
                    <div className='filter-group'>
                      <label className='filter-label'>Sort By</label>
                      <select
                        name='sortBy'
                        value={filters.sortBy}
                        onChange={handleFilterChange}
                        className='filter-input'
                      >
                        <option value='cost'>Cost</option>
                        <option value='impressions'>Impressions</option>
                        <option value='clicks'>Clicks</option>
                        <option value='conversions'>Conversions</option>
                        <option value='name'>Campaign Name</option>
                        <option value='ctr'>CTR</option>
                      </select>
                    </div>
                    <div className='filter-group'>
                      <label className='filter-label'>Sort Order</label>
                      <select
                        name='sortOrder'
                        value={filters.sortOrder}
                        onChange={handleFilterChange}
                        className='filter-input'
                      >
                        <option value='desc'>High to Low</option>
                        <option value='asc'>Low to High</option>
                      </select>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Regular report filters */}
                    <div className='filter-group'>
                      <label className='filter-label'>Clicks Range</label>
                      <div className='range-group'>
                        <input
                          name='clicksMin'
                          placeholder='Min'
                          value={filters.clicksMin}
                          onChange={handleFilterChange}
                          className='filter-input'
                          type='number'
                        />
                        <input
                          name='clicksMax'
                          placeholder='Max'
                          value={filters.clicksMax}
                          onChange={handleFilterChange}
                          className='filter-input'
                          type='number'
                        />
                      </div>
                    </div>
                    <div className='filter-group'>
                      <label className='filter-label'>Impressions Range</label>
                      <div className='range-group'>
                        <input
                          name='impressionsMin'
                          placeholder='Min'
                          value={filters.impressionsMin}
                          onChange={handleFilterChange}
                          className='filter-input'
                          type='number'
                        />
                        <input
                          name='impressionsMax'
                          placeholder='Max'
                          value={filters.impressionsMax}
                          onChange={handleFilterChange}
                          className='filter-input'
                          type='number'
                        />
                      </div>
                    </div>
                    <div className='filter-group'>
                      <label className='filter-label'>Report Date</label>
                      <input
                        name='date'
                        type='date'
                        value={filters.date}
                        onChange={handleFilterChange}
                        className='filter-input'
                      />
                    </div>
                  </>
                )}
                <div className='filter-group'>
                  <button
                    className='clear-filters-btn'
                    onClick={clearAllFilters}
                  >
                    <i className='fa fa-times'></i>
                    Clear All
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Summary section for customer campaigns */}
          {isCustomerCampaignView && data?.summary && (
            <div className='summary-section'>
              {data.period && (
                <div className='summary-period'>
                  <i className='fa fa-calendar'></i>
                  <span>{data.period}</span>
                </div>
              )}
              <div className='summary-cards'>
                <div className='summary-card'>
                  <div className='summary-label'>Total Campaigns</div>
                  <div className='summary-value'>
                    {data.summary.totalCampaigns || 0}
                  </div>
                </div>
                <div className='summary-card'>
                  <div className='summary-label'>Active Campaigns</div>
                  <div className='summary-value'>
                    {data.summary.activeCampaigns || 0}
                  </div>
                </div>
                <div className='summary-card'>
                  <div className='summary-label'>Total Cost</div>
                  <div className='summary-value'>
                    ₹{' '}
                    {data.summary.totalCost
                      ? Number.parseFloat(data.summary.totalCost).toFixed(2)
                      : '0.00'}
                  </div>
                </div>
                <div className='summary-card'>
                  <div className='summary-label'>Total Clicks</div>
                  <div className='summary-value'>
                    {(data.summary.totalClicks || 0).toLocaleString()}
                  </div>
                </div>
                <div className='summary-card'>
                  <div className='summary-label'>Total Impressions</div>
                  <div className='summary-value'>
                    {(data.summary.totalImpressions || 0).toLocaleString()}
                  </div>
                </div>
                <div className='summary-card'>
                  <div className='summary-label'>Total Conversions</div>
                  <div className='summary-value'>
                    {(data.summary.totalConversions || 0).toLocaleString()}
                  </div>
                </div>
                <div className='summary-card'>
                  <div className='summary-label'>Avg CTR</div>
                  <div className='summary-value'>
                    {data.summary.averageCtr
                      ? Number.parseFloat(data.summary.averageCtr).toFixed(2) +
                        '%'
                      : '0.00%'}
                  </div>
                </div>
                <div className='summary-card'>
                  <div className='summary-label'>Cost per Conversion</div>
                  <div className='summary-value'>
                    ₹{' '}
                    {data.summary.costPerConversion
                      ? Number.parseFloat(
                          data.summary.costPerConversion
                        ).toFixed(2)
                      : '0.00'}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className='google-table-container'>
            {isLoading ? (
              <div className='loading-container'>
                <div className='loading-spinner'></div>
                <p>Loading Google Ads reports...</p>
              </div>
            ) : (
              <table className='google-table'>
                <thead className='google-table-header'>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {header.column.getIsSorted() && (
                            <span className='sort-icon'>
                              {header.column.getIsSorted() === 'desc' ? (
                                <i className='fa fa-caret-down'></i>
                              ) : (
                                <i className='fa fa-caret-up'></i>
                              )}
                            </span>
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className='google-table-body'>
                  {table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      className={getRowHighlightClass(row.original)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {table.getRowModel().rows.length === 0 && !isLoading && (
              <div className='loading-container'>
                <i
                  className='fa fa-search'
                  style={{
                    fontSize: '24px',
                    marginBottom: '12px',
                    opacity: 0.5,
                  }}
                ></i>
                <p>
                  {isCustomerCampaignView
                    ? 'No campaigns found'
                    : 'No Google Ads reports found'}
                </p>
              </div>
            )}
          </div>

          {!isLoading && data && (
            <div className='pagination-container'>
              <div className='pagination-info'>
                {isCustomerCampaignView ? (
                  <>
                    Showing {(filters.page - 1) * filters.limit + 1} to{' '}
                    {Math.min(
                      filters.page * filters.limit,
                      data.pagination?.totalItems || 0
                    )}{' '}
                    of {data.pagination?.totalItems || 0} campaigns
                    {data.period && <span> • {data.period}</span>}
                  </>
                ) : (
                  <>
                    Showing {(filters.page - 1) * filters.limit + 1} to{' '}
                    {Math.min(
                      filters.page * filters.limit,
                      data.totalCount || 0
                    )}{' '}
                    of {data.totalCount || 0} results
                  </>
                )}
              </div>
              <div className='pagination-controls'>
                <button
                  className='pagination-button'
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  <i className='fa fa-angle-left'></i>
                </button>

                {Array.from(
                  {
                    length: Math.min(
                      5,
                      isCustomerCampaignView
                        ? data.pagination?.totalPages || 1
                        : data.totalPages || 1
                    ),
                  },
                  (_, i) => {
                    const totalPages = isCustomerCampaignView
                      ? data.pagination?.totalPages || 1
                      : data.totalPages || 1;
                    const pageIndex = Math.max(1, filters.page - 2) + i;
                    if (pageIndex > totalPages) return null;
                    return (
                      <button
                        key={pageIndex}
                        className={`pagination-button ${
                          pageIndex === filters.page ? 'active' : ''
                        }`}
                        onClick={() =>
                          setFilters((f) => ({ ...f, page: pageIndex }))
                        }
                      >
                        {pageIndex}
                      </button>
                    );
                  }
                )}

                <button
                  className='pagination-button'
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  <i className='fa fa-angle-right'></i>
                </button>

                <div className='page-size-selector'>
                  <span>Show:</span>
                  <select
                    className='page-size-select'
                    value={filters.limit}
                    onChange={(e) =>
                      setFilters((f) => ({
                        ...f,
                        limit: Number(e.target.value),
                        page: 1,
                      }))
                    }
                  >
                    {[10, 15, 25, 50].map((pageSize) => (
                      <option key={pageSize} value={pageSize}>
                        {pageSize}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Benchmark Edit Modal */}
      {benchmarkModal.isOpen && (
        <div
          className='benchmark-modal-overlay'
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setBenchmarkModal({ isOpen: false, campaign: null });
            }
          }}
        >
          <div className='benchmark-modal'>
            <div className='modal-header'>
              <h2 className='modal-title'>Edit Benchmark</h2>
              <button
                className='modal-close'
                onClick={() =>
                  setBenchmarkModal({ isOpen: false, campaign: null })
                }
              >
                &times;
              </button>
            </div>
            <div className='modal-body'>
              <div className='form-group'>
                <label htmlFor='benchmark' className='form-label'>
                  Campaign:{' '}
                  {benchmarkModal.campaign?.campaign_name ||
                    benchmarkModal.campaign?.campaign_id}
                </label>
              </div>
              <div className='form-group'>
                <label htmlFor='benchmark' className='form-label'>
                  Benchmark Value:
                </label>
                <input
                  id='benchmark'
                  type='text'
                  className='benchmark-input'
                  value={benchmarkValue}
                  onChange={(e) => setBenchmarkValue(e.target.value)}
                  placeholder='Enter benchmark value'
                  autoFocus
                />
              </div>
            </div>
            <div className='modal-footer'>
              <button
                className='save-benchmark-btn'
                onClick={async () => {
                  if (!benchmarkModal.campaign) return;

                  console.log('Saving benchmark for campaign:', benchmarkModal);

                  setIsSavingBenchmark(true);
                  try {
                    await updateBenchmark(
                      // account,
                      filters.selectedCustomer,
                      benchmarkModal.campaign.id,
                      benchmarkValue
                    );
                    // Refresh data after successful update
                    queryClient.invalidateQueries([
                      'google-ads-customer-campaigns',
                    ]);
                    setBenchmarkModal({ isOpen: false, campaign: null });
                    setBenchmarkValue('');
                  } catch (error) {
                    console.error('Failed to update benchmark:', error);
                    alert('Failed to update benchmark. Please try again.');
                  } finally {
                    setIsSavingBenchmark(false);
                  }
                }}
                disabled={isSavingBenchmark}
              >
                {isSavingBenchmark ? 'Saving...' : 'Save'}
              </button>
              <button
                className='cancel-benchmark-btn'
                onClick={() =>
                  setBenchmarkModal({ isOpen: false, campaign: null })
                }
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EnhancedGoogleAdsTable;
